import { type FC, useContext, useRef, useState } from "react";

import { type BoxProps, Column } from "@hightouch/htui";

import { WorkspaceSelector } from "src/components/layout/nav/workspace-selector";
import { MiniNavSidebarContext } from "src/components/layout/nav/mini-nav/mini-nav-context";
import { useNavWidth } from "src/contexts/nav-width-context";
import { useIsAgenticSurfaceActive } from "src/hooks/use-agentic-home-enabled";

import { AmpNavSections } from "./amp-nav";
import { NavHoverProvider } from "./nav-hover";
import { ClassicSettingsLinks, Footer } from "./footer";
import { AmpNavHeader, NavHeader } from "./header";
import { Activate } from "./sections/activate";
import { Agents } from "./sections/agents";
import { CustomerStudio } from "./sections/customer-studio";
import { DecisionEngines } from "./sections/decision-engine";
import { EventCollection } from "./sections/event-collection";
import { GetStarted } from "./sections/get-started";
import { Home } from "./sections/home";
import { IdentityResolution } from "./sections/identity-resolution";
import { Integrations } from "./sections/integrations";
import { Intelligence } from "./sections/intelligence";
import { Projects, ProjectsNav } from "./sections/projects";
import { useShowProjectsNav } from "./sections/hooks/projects";
import { useIsMiniNavEligible } from "./mini-nav/use-is-mini-nav-eligible";
import { useActiveSection } from "./utils";
import { LifecycleStudio } from "./sections/lifecycle-studio";

import { AdStudio } from "./sections/ad-studio";
import { ContextHub } from "./sections/context-hub";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import { CEP } from "./sections/cep";

import { SpaceSelector, useSpaceContext } from "src/components/spaces";

export const Nav: FC<{
  /** Inside the mini-nav's sidebar, which places and sizes it, docked or over the page. */
  isFramed?: boolean;
}> = ({ isFramed = false }) => {
  const { canNavOpen, isNavOpen, setIsNavOpen } = useNavWidth();
  const miniNav = useContext(MiniNavSidebarContext);
  // Held through the access check as the last load had it, so the product nav's frame never shows the classic nav's sections first.
  const hasProductTabs = useIsMiniNavEligible();
  const [isCollapsedNavHovered, setIsCollapsedNavHovered] = useState(false);
  const {
    isSpacesEnabled,
    selectedSpace,
    availableSpaces,
    isUserSpaceScoped,
    selectSpace,
  } = useSpaceContext();

  function handleDeadSpaceClick(e: React.MouseEvent<HTMLDivElement>) {
    // Only trigger on dead space (not on nav items/sections)
    if (e.target !== e.currentTarget) return;
    // The peek docks on a press there, as the collapsed classic rail opens on one; a click with no pointer position is a keyboard's.
    if (miniNav?.overlay === "peek") {
      if (e.detail > 0) miniNav.toggleDocked("toggle", false);
      return;
    }
    if (!isNavOpen && canNavOpen) {
      setIsNavOpen(true);
    }
  }

  return (
    <NavContainer
      isNavOpen={isNavOpen}
      isFramed={isFramed}
      onClick={handleDeadSpaceClick}
      onCollapsedHoverChange={setIsCollapsedNavHovered}
    >
      <Column px={2} gap={hasProductTabs ? 2 : 4}>
        {hasProductTabs ? (
          <AmpNavHeader />
        ) : (
          <NavHeader
            showToggleHoverState={!isNavOpen && isCollapsedNavHovered}
          />
        )}
        <Column gap={2}>
          {!hasProductTabs && <WorkspaceSelector />}
          {isSpacesEnabled && availableSpaces.length > 0 && (
            <SpaceSelector
              selectedSpace={selectedSpace}
              availableSpaces={availableSpaces}
              isUserSpaceScoped={isUserSpaceScoped}
              isNavOpen={isNavOpen}
              selectSpace={selectSpace}
            />
          )}
        </Column>
      </Column>
      {/* AMP v2 users get the AMP nav, unless they chose the classic one. */}
      {hasProductTabs ? (
        <AmpNavSections onClick={handleDeadSpaceClick} />
      ) : (
        <NavSections onClick={handleDeadSpaceClick} />
      )}
    </NavContainer>
  );
};

type NavContainerProps = Pick<BoxProps, "children" | "onClick"> & {
  isNavOpen: boolean;
  isFramed: boolean;
  onCollapsedHoverChange: (hovered: boolean) => void;
};

const NavContainer = ({
  isNavOpen,
  isFramed,
  children,
  onClick,
  onCollapsedHoverChange,
}: NavContainerProps) => {
  const { navWidth } = useNavWidth();
  const navRef = useRef<HTMLDivElement>(null);
  const hasTransparentBackground = useIsAgenticSurfaceActive();

  return (
    <Column
      ref={navRef}
      as="aside"
      cursor={{ base: "default", lg: isNavOpen ? "default" : "e-resize" }}
      background={hasTransparentBackground ? "transparent" : "base.background"}
      display="flex"
      flexDir="column"
      gap={2}
      height={isFramed ? "100%" : "100vh"}
      overflow="hidden"
      position={isFramed ? "relative" : "sticky"}
      top={0}
      width={isFramed ? "100%" : navWidth}
      maxWidth={isFramed ? undefined : navWidth}
      py={4}
      pl={0.5}
      zIndex={2}
      onClick={onClick}
      onMouseEnter={() => !isNavOpen && onCollapsedHoverChange(true)}
      onMouseLeave={() => onCollapsedHoverChange(false)}
    >
      {children}
    </Column>
  );
};

const NavSections = ({ onClick }: Pick<BoxProps, "onClick">) => {
  const activeState = useActiveSection();
  const { isNavOpen } = useNavWidth();
  const scrollFade = useScrollFade();
  const showProjectsNav = useShowProjectsNav();

  return (
    <Column
      as="nav"
      aria-label="Main navigation"
      flex={1}
      gap={2}
      justifyContent="space-between"
      minHeight={0}
      width="100%"
      onClick={onClick}
    >
      {/* Around the scrolling list, so the gaps between its rows count as the list and the pill slides across them. */}
      <NavHoverProvider>
        <Column
          ref={scrollFade.ref}
          cursor="default"
          gap={3}
          overflowY="auto"
          px={2}
          py={2}
          sx={{
            ...scrollFade.sx,
            // Hide scrollbar when collapsed to prevent icon alignment issues
            ...(!isNavOpen && {
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }),
          }}
        >
          {showProjectsNav ? (
            <ProjectsNav />
          ) : (
            <>
              <GetStarted />
              <Home />
              <Projects />
              <Agents {...activeState} />
              <Activate {...activeState} />
              <CustomerStudio {...activeState} />
              <LifecycleStudio {...activeState} />
              <AdStudio {...activeState} />
              <ContextHub {...activeState} />
              <DecisionEngines {...activeState} />
              <CEP {...activeState} />
              <Intelligence {...activeState} />
              <IdentityResolution />
              <Integrations {...activeState} />
              <EventCollection {...activeState} />
            </>
          )}
        </Column>
      </NavHoverProvider>
      <NavHoverProvider>
        <Footer>
          <ClassicSettingsLinks {...activeState} />
        </Footer>
      </NavHoverProvider>
    </Column>
  );
};
